import { ActionType } from "../../../constants/ActionTypes";

// ---------------------------------------------------------------------------
// Logos read ownership + context binding (the P1 remediation; mirrors the
// approved per-store pages pattern — read-only reference,
// src/reducers/ui/pagesReducer.js isOwnedTerminal/invalidateTrackedRequests —
// minimal corresponding logos scope). The active owning request lives IN THIS
// SLICE's state (never a module-global "latest" pointer, so independent stores
// never block each other's requests), and readScope records the company/project
// context the current data belongs to:
// - A SAME-context refresh BEGIN keeps the last-known data on display (a failed
//   refresh must not wipe the URLs the user still sees — the validator's
//   "same-context last-known-data" criterion).
// - A DIFFERENT company/project context BEGIN clears the moved-on context's
//   data, ownership and flags (tenant isolation: A's logos are never displayed
//   while B awaits its read, nor restored when B's read fails).
// ---------------------------------------------------------------------------
const normalizeIdentifier = value => (value === null || value === undefined ? null : String(value));

const isSameLogosReadContext = (previousScope, nextScope) => {
	const previous = previousScope || {};
	const next = nextScope || {};
	return (
		normalizeIdentifier(previous.companyId) === normalizeIdentifier(next.companyId) &&
		normalizeIdentifier(previous.headerProjectId) === normalizeIdentifier(next.headerProjectId) &&
		normalizeIdentifier(previous.selectedProjectId) === normalizeIdentifier(next.selectedProjectId)
	);
};

// A terminal (SUCCESS/ERROR) is accepted only from the request that currently
// owns the logos read stream. A tracked terminal (meta.requestId) must match the
// active owner exactly — with no active owner it is a superseded, duplicate or
// invalidated completion and is rejected. A legacy terminal without meta may
// never bypass an active tracked request; with no tracked request active it
// keeps its exact legacy behavior.
const isOwnedLogosTerminal = (state, action) => {
	const owner = state.readRequest;
	if (action.meta && action.meta.requestId !== undefined) {
		return !!owner && owner.requestId === action.meta.requestId;
	}
	return !owner;
};

// Dropping the read ownership of a moved-on context also ends its loading flag:
// its terminal is rejected, so the spinner must not stay armed.
const invalidateLogosRead = state => ({
	...state,
	readRequest: null,
	inLoadingProcess: false,
});

export default function logosReducer(state = {}, action = {}) {
	switch (action.type) {
		case ActionType.GET_LOGOS_SUCCESS:
			if (!isOwnedLogosTerminal(state, action)) {
				return state;
			}
			return {
				...state,
				readRequest: null,
				inLoadingProcess: false,
				data: action.payload,
			};
		case ActionType.GET_LOGOS_ERROR:
			if (!isOwnedLogosTerminal(state, action)) {
				return state;
			}
			return {
				...state,
				readRequest: null,
				inLoadingProcess: false,
			};
		case ActionType.SET_LOGOS_ERROR:
			return {
				...state,
				error: action.payload,
				inSavingProcess: false,
			};
		case ActionType.SET_LOGOS_SUCCESS:
			return {
				...state,
				inSavingProcess: false,
				data: {
					...state.data,
					logoLink: action.payload,
				},
			};
		case ActionType.GET_LOGOS_BEGIN: {
			const tracked = action.meta && action.meta.requestId !== undefined ? action.meta : null;
			let ownership = state;
			let data = state.data;
			if (tracked) {
				// A read issued for a DIFFERENT company/project context must not keep the
				// moved-on context's logos on display while it loads: clear its data and
				// ownership (the old read's late terminal is then rejected, so it can
				// neither restore A's data nor clear this read's loading flag). A
				// SAME-context refresh (or a first tracked read after legacy seeding,
				// whose data context is unknown) keeps the last-known data.
				if (state.readScope && !isSameLogosReadContext(state.readScope, tracked.scope)) {
					ownership = invalidateLogosRead(ownership);
					data = [];
				}
				ownership = {
					...ownership,
					readScope: tracked.scope,
					readRequest: { requestId: tracked.requestId, scope: tracked.scope },
				};
			}
			return {
				...ownership,
				inLoadingProcess: true,
				inSavingProcess: false,
				data,
			};
		}
		case ActionType.SET_LOGOS_BEGIN:
			return {
				...state,
				inSavingProcess: true,
			};
		case ActionType.UPLOAD_LOGOS_BEGIN:
			return {
				...state,
				[`${action.logoType}Loading`]: true,
			};
		case ActionType.UPLOAD_LOGOS_ERROR:
			return {
				...state,
				[`${action.logoType}Loading`]: false,
			};
		case ActionType.UPLOAD_LOGOS_SUCCESS:
			return {
				...state,
				[`${action.logoType}Loading`]: false,
				// The upload success action carries no replacement URL (the post-upload
				// refetch read is authoritative): keep the last-known data instead of
				// nulling `${logoType}Url`.value. A real replacement (if an action ever
				// carries one) still writes through.
				data:
					action.logo === undefined
						? state.data
						: {
								...state.data,
								[`${action.logoType}Url`]: {
									...state.data[`${action.logoType}Url`],
									value: action.logo,
								},
							},
			};
		case ActionType.GET_LOGOS_SETTINGS_BEGIN:
			return {
				...state,
				settingsLoading: true,
			};
		case ActionType.GET_LOGOS_SETTINGS_ERROR:
			return {
				...state,
				settingsLoading: false,
			};

		case ActionType.GET_LOGO_SETTINGS_SUCCESS:
			return {
				...state,
				settings: action.payload,
				settingsLoading: false,
			};
		case ActionType.CHANGE_LOGO_SETTINGS_SUCCESS:
			return {
				...state,
				settings: action.payload,
				settingsLoading: false,
			};

		// Lifecycle: the customization header cleanup resets the selected project on
		// unmount and the project selection can change (deep link, project switch). A
		// read issued for the previously selected context may never land afterwards; its
		// loading flag ends with it. A refresh of the SAME project (identical id,
		// normalized) keeps every valid in-flight request running; GET_PROJECT_BEGIN
		// alone never invalidates anything.
		case ActionType.RESET_SELECTED_PROJECT:
			return invalidateLogosRead(state);
		case ActionType.GET_PROJECT_SUCCESS: {
			const nextProjectId = normalizeIdentifier(action.payload && action.payload.id);
			const owner = state.readRequest;
			const movedOn =
				!!owner &&
				normalizeIdentifier(owner.scope && owner.scope.selectedProjectId) !== null &&
				normalizeIdentifier(owner.scope && owner.scope.selectedProjectId) !== nextProjectId;
			if (!movedOn) {
				return state;
			}
			return invalidateLogosRead(state);
		}
		default:
			return state;
	}
}
